/**
 * Selects the Box folders owned by one durable Vault connector feed.
 *
 * Selected folders are summarized compactly, while the existing browser
 * remains the only authority for navigation and remote selection.
 */
import { useState } from 'react'
import { Plus, X } from 'lucide-react'

import {
  Button,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@/components/ui'
import { cn } from '@/lib/utils'
import { FolderGlyph } from '@/subsystems/vault/components/VaultItemIcon'
import type {
  ConnectorInitialSync,
  ConnectorSchedule,
} from '../../types'
import type { BoxEntry } from '../services/source-api'
import { BoxFolderBrowser } from './BoxFolderBrowser'

export function BoxContentScope({
  token,
  selected,
  onSelectedChange,
  initialSync,
  onInitialSyncChange,
  schedule,
  onScheduleChange,
  folderLocked = false,
}: {
  token: string | null
  selected: BoxEntry[]
  onSelectedChange: (selected: BoxEntry[]) => void
  initialSync: ConnectorInitialSync
  onInitialSyncChange: (value: ConnectorInitialSync) => void
  schedule: ConnectorSchedule
  onScheduleChange: (schedule: ConnectorSchedule) => void
  folderLocked?: boolean
}) {
  const [choosing, setChoosing] = useState(false)
  const mode = schedule === 'manual' ? 'once' : 'sync'
  const selectMode = (next: 'once' | 'sync') => {
    if (next === 'once') {
      onInitialSyncChange('all')
      onScheduleChange('manual')
      return
    }
    if (schedule === 'manual') onScheduleChange('hourly')
  }
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-xs font-medium text-slate-700">Import mode</Label>
        <RadioGroup
          value={mode}
          onValueChange={value => selectMode(value as 'once' | 'sync')}
          className="grid gap-2.5 sm:grid-cols-2"
        >
          <ModeChoice value="once" selected={mode === 'once'} title="One-time import">
            Copy the selected folders once
          </ModeChoice>
          <ModeChoice value="sync" selected={mode === 'sync'} title="Keep folders in sync">
            Pick up new and changed files on a schedule
          </ModeChoice>
        </RadioGroup>
      </div>

      {mode === 'sync' && !folderLocked && (
        <div className="space-y-2">
          <Label className="text-xs font-medium text-slate-700">First scheduled run</Label>
          <RadioGroup
            value={initialSync}
            onValueChange={value => onInitialSyncChange(value as ConnectorInitialSync)}
            className="flex flex-wrap gap-2"
          >
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-700">
              <RadioGroupItem value="all" /> Transfer current contents
            </label>
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-700">
              <RadioGroupItem value="future" /> Start with future changes
            </label>
          </RadioGroup>
        </div>
      )}

      <div className="overflow-hidden rounded-[9px] border border-slate-200 bg-white">
        <div className="flex items-center justify-between gap-3 px-3 py-2">
          <span className="text-xs font-semibold text-slate-800">
            {folderLocked
              ? `Synced folders (${selected.length})`
              : selected.length
                ? `${selected.length} folder${selected.length === 1 ? '' : 's'} selected`
                : 'No folders selected'}
          </span>
          {!folderLocked && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 rounded-[7px] px-2.5 text-xs"
              onClick={() => setChoosing(current => !current)}
            >
              <Plus className="mr-1 h-3.5 w-3.5" />
              {choosing ? 'Close browser' : 'Choose in Box'}
            </Button>
          )}
        </div>

        {selected.map(folder => (
          <div key={folder.id} className="flex items-center gap-2.5 border-t border-slate-100 bg-slate-50/50 px-3 py-2">
            <FolderGlyph className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate text-[13px] text-slate-800">
              {folder.name}
            </span>
            <span className="shrink-0 text-xs text-slate-400">Folder</span>
            {!folderLocked && (
              <button
                type="button"
                aria-label={`Remove ${folder.name}`}
                className="rounded text-slate-400 transition-colors hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
                onClick={() => onSelectedChange(selected.filter(item => item.id !== folder.id))}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}

        {choosing && !folderLocked && token && (
          <div className="h-72 border-t border-slate-100 p-3">
            <BoxFolderBrowser
              token={token}
              selected={selected}
              onSelectedChange={onSelectedChange}
            />
          </div>
        )}
      </div>
    </div>
  )
}

function ModeChoice({
  value,
  selected,
  title,
  children,
}: {
  value: 'once' | 'sync'
  selected: boolean
  title: string
  children: string
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-center gap-3 rounded-[9px] border px-3.5 py-2.5 transition-colors',
        selected
          ? 'border-[1.5px] border-slate-900 bg-white'
          : 'border-slate-200 hover:bg-slate-50',
      )}
    >
      <RadioGroupItem value={value} />
      <span className="min-w-0">
        <span className="block text-[13px] font-semibold text-slate-900">{title}</span>
        <span className="block truncate text-xs text-slate-500">{children}</span>
      </span>
    </label>
  )
}
